// Notificacao de mensagem nova no aplicativo do cliente (DA-532), lado do navegador.
//
// A regra que decide TUDO nesta tela: **no Android o aviso funciona em aba comum;
// no iPhone so funciona com o app na TELA DE INICIO**. Confirmado na documentacao
// da Apple, que separa as duas plataformas com todas as letras: web push vale pra
// "Home Screen web apps in iOS 16.4 or later" e pra "Webpages in Safari... for
// macOS". Aba do Safari no iPhone nao entra.
//
// Consequencia pratica: metade da base nao precisa instalar nada, e a outra metade
// precisa de um passo a passo. Por isso este arquivo separa "nao da' pra notificar"
// de "da', mas antes tem que por na tela de inicio" — sao mensagens diferentes, e
// tratar as duas como "seu aparelho nao suporta" faria o cliente de iPhone desistir
// de um recurso que ele TEM.

const CAMINHO_SW = '/sw-app.js';
const ESCOPO_SW = '/app';

/**
 * Abaixo disto, a resposta do pedido de permissao nao passou por gente nenhuma.
 *
 * Nao e' numero fino: ler a pergunta, entender e tocar leva segundos, e navegador
 * que responde sozinho volta em dezenas de milissegundos. Qualquer valor entre uns
 * 300ms e 1s separaria os dois casos igual — 700ms fica no meio, longe das duas
 * pontas, e nenhuma decisao depende de acertar a fronteira.
 */
const LIMIAR_RESPOSTA_HUMANA_MS = 700;

/** iPhone/iPad. O iPad moderno mente que e' Mac — daí o segundo teste. */
export function ehApple(): boolean {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/** O app esta rodando como atalho (tela de inicio), nao como aba do navegador. */
export function estaInstalado(): boolean {
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // Safari do iOS nao implementa `display-mode`; ele expoe esta propriedade.
  return (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** Este navegador sabe receber push AGORA, do jeito que esta aberto. */
export function suportado(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * "Nao da' AGORA, mas daria se estivesse na tela de inicio."
 *
 * ⚠️ E' o caso do iPhone em aba do Safari — onde o proprio `PushManager` nem
 * existe, entao `suportado()` responde `false`. Sem esta distincao a tela diria
 * "seu aparelho nao suporta" pra um aparelho que suporta, e o cliente nunca
 * descobriria que falta um passo de 10 segundos.
 */
export function precisaInstalarPrimeiro(): boolean {
  return !suportado() && ehApple() && !estaInstalado();
}

export type EstadoPermissao = 'concedida' | 'negada' | 'perguntar';

export function permissao(): EstadoPermissao {
  if (!('Notification' in window)) return 'perguntar';
  if (Notification.permission === 'granted') return 'concedida';
  if (Notification.permission === 'denied') return 'negada';
  return 'perguntar';
}

/**
 * Registra o service worker.
 *
 * ⚠️ Roda na ABERTURA da tela, nao no clique — e isso e' exigencia da Apple, nao
 * organizacao: ela manda chamar a inscricao "immediately from the gesture's event
 * handler code". Registrar o worker dentro do clique gastaria a interacao do
 * usuario esperando o registro, e o `subscribe` chegaria depois da janela — falhando
 * so no iPhone, so as vezes, que e' o pior modo de falhar que existe.
 *
 * ⚠️ O worker NAO tem `fetch` (ver `public/sw-app.js`): ele nao guarda nem serve
 * pagina, entao registrar aqui nao reabre o risco de tela velha que a [DA-054]
 * evitou ao decidir "PWA sem service worker".
 */
export async function prepararWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!suportado()) return null;
  try {
    return await navigator.serviceWorker.register(CAMINHO_SW, { scope: ESCOPO_SW });
  } catch {
    return null;
  }
}

/** base64url (como a chave VAPID viaja) -> bytes, que e' o que o navegador aceita. */
function chaveParaBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const preenchido = base64Url.padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), '=');
  const bruto = atob(preenchido.replace(/-/g, '+').replace(/_/g, '/'));
  // ⚠️ O buffer vem declarado: `new Uint8Array(n)` produz `Uint8Array<ArrayBufferLike>`,
  // que o tipo do `applicationServerKey` recusa (ele exige `ArrayBuffer`, nao
  // `SharedArrayBuffer`). Erro so de tipo — mas o typecheck e' portao de deploy.
  const bytes = new Uint8Array(new ArrayBuffer(bruto.length));
  for (let i = 0; i < bruto.length; i++) bytes[i] = bruto.charCodeAt(i);
  return bytes;
}

export interface InscricaoPronta {
  endpoint: string;
  p256dh: string;
  auth: string;
}

function serializar(sub: PushSubscription): InscricaoPronta | null {
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) return null;
  return { endpoint: json.endpoint, p256dh, auth };
}

/** A inscricao que ja existe neste aparelho, se existir. */
export async function inscricaoAtual(): Promise<InscricaoPronta | null> {
  if (!suportado()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration(ESCOPO_SW);
    const sub = await reg?.pushManager.getSubscription();
    return sub ? serializar(sub) : null;
  } catch {
    return null;
  }
}

/**
 * `detalhe` carrega a MEDICAO crua do pedido de permissao (estado antes, resposta,
 * estado depois, tempo). Existe porque em 14/08/2026 o Android recusou o aviso do
 * Ruan com todos os quatro portoes abertos — Android liberado pro Chrome, chave
 * geral do Chrome ligada, "abrir todas as solicitacoes" marcado, site fora da lista
 * de bloqueados — e nao havia como saber, de fora, o que o navegador tinha
 * respondido. Sem numero, o diagnostico vira palpite; ⛔ nao remover isto sem ter
 * outra forma de ler a resposta do navegador no aparelho do cliente.
 */
export class ErroPush extends Error {
  detalhe?: string;
  constructor(mensagem: string, detalhe?: string) {
    super(mensagem);
    this.detalhe = detalhe;
  }
}

/**
 * Inscreve este aparelho **sem pedir permissao nenhuma**.
 *
 * ⚠️ Existe separada de propósito, e a separacao conserta um defeito real: pedir
 * permissao que a pessoa JA deu nao e' inofensivo. A Apple exige que
 * `requestPermission()` saia de um gesto do usuario; chamada na abertura da tela
 * ela pode ser recusada de saida, e a recusa nao diz "ja esta concedida", diz
 * "nao pode agora". Quem tratasse isso como falha mostraria a faixa de "Ativar
 * avisos" **a cada abertura**, pedindo de novo o que ja estava ligado.
 *
 * So chame com a permissao ja concedida; quem ainda nao tem usa `ativar`.
 */
export async function inscrever(chavePublica: string): Promise<InscricaoPronta> {
  if (!suportado()) throw new ErroPush('Este navegador nao recebe avisos.');
  if (!chavePublica) throw new ErroPush('O servidor ainda nao esta pronto pra enviar avisos.');

  const reg = (await navigator.serviceWorker.getRegistration(ESCOPO_SW)) ?? (await prepararWorker());
  if (!reg) throw new ErroPush('Nao consegui preparar os avisos neste aparelho.');

  // Reaproveita a inscricao que ja existe. Inscrever de novo por cima geraria
  // endereco novo e deixaria o anterior orfao no servidor — que so morreria na
  // primeira falha de envio, ou seja, depois de um aviso perdido.
  const existente = await reg.pushManager.getSubscription();
  const sub = existente ?? (await reg.pushManager.subscribe({
    // Obrigatorio: sem isso o navegador recusa. E' o que garante que todo push
    // recebido vira notificacao VISIVEL — a Apple revoga a permissao do site que
    // manda push silencioso.
    userVisibleOnly: true,
    applicationServerKey: chaveParaBytes(chavePublica),
  }));

  const pronta = serializar(sub);
  if (!pronta) throw new ErroPush('A inscricao veio incompleta deste aparelho.');
  return pronta;
}

/**
 * Liga o aviso neste aparelho. **Tem que ser chamada de dentro do clique.**
 *
 * A ordem e' a da documentacao da Apple: pedir a permissao e, com ela na mao,
 * inscrever em seguida — sem nada demorado no meio. Por isso o registro do worker
 * ja aconteceu antes (`prepararWorker`), e por isso quem JA tem permissao nao
 * passa por aqui: vai direto em `inscrever`.
 */
export async function ativar(chavePublica: string): Promise<InscricaoPronta> {
  if (!suportado()) throw new ErroPush('Este navegador nao recebe avisos.');

  if (permissao() !== 'concedida') {
    // ⚠️ **O TEMPO e' o que separa duas causas que a API mistura.** `requestPermission`
    // devolve `denied` tanto quando a pessoa tocou em "Nao permitir" quanto quando o
    // proprio navegador respondeu sozinho — e as duas situacoes pedem instrucoes
    // OPOSTAS. Gente leva segundos pra ler e decidir; navegador responde em
    // milissegundos. Abaixo do limiar, ninguem tocou em nada, e mandar a pessoa
    // "desbloquear nas configuracoes do site" e' manda-la procurar um bloqueio que
    // nao existe em lista nenhuma (foi o beco que custou 40 minutos em 14/08/2026).
    const antes = permissao();
    const t0 = performance.now();
    const resposta = await Notification.requestPermission();
    const ms = Math.round(performance.now() - t0);
    const detalhe = `permissao ${antes} -> resposta ${resposta} -> ${permissao()} em ${ms}ms`;
    const semHumano = ms < LIMIAR_RESPOSTA_HUMANA_MS;

    if (resposta === 'denied') {
      throw new ErroPush(
        semHumano
          ? 'O navegador respondeu por voce, sem mostrar a pergunta. Isso costuma acontecer depois de alguns pedidos sem resposta: ele passa a recusar sozinho por um tempo. Limpe os dados deste site no navegador, ou tente de novo daqui a alguns dias.'
          : 'Voce recusou os avisos. Libere nas configuracoes deste site no navegador e tente de novo.',
        detalhe,
      );
    }
    if (resposta !== 'granted') {
      throw new ErroPush(
        semHumano
          ? 'O navegador fechou a pergunta sozinho. Se apareceu um aviso pequeno na barra de endereco, no alto da tela, toque nele pra responder.'
          : 'Avisos nao foram liberados.',
        detalhe,
      );
    }
  }

  return inscrever(chavePublica);
}

/**
 * Apaga o numero da bolinha do icone.
 *
 * Chamado quando o cliente esta OLHANDO a conversa — quem viu, leu. Falha em
 * silencio: no Android e em navegador de terceiro no iPhone a funcao nao existe, e
 * isso nao pode virar erro numa tela de chat.
 */
export function limparBadge(): void {
  const nav = navigator as Navigator & { clearAppBadge?: () => Promise<void> };
  nav.clearAppBadge?.().catch(() => {});
}

/** Desliga o aviso neste aparelho. Devolve o endereco que saiu, pra avisar o servidor. */
export async function desativar(): Promise<string | null> {
  if (!suportado()) return null;
  const reg = await navigator.serviceWorker.getRegistration(ESCOPO_SW);
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  return endpoint;
}
